import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { COMMUNITY_CONDUCT_VERSION } from '../constants/communityConduct';

export const MAX_MESSAGE_LENGTH = 2000;

// Memberships, not cached UI flags, determine whether a table has a chat.
export function canUseTableChat(group, userId) {
  return Boolean(userId && group?.status === 'ACTIVE'
    && group.memberships?.[userId]?.status === 'JOINED'
    && group.attendeeIds?.includes(userId)
    && group.attendeeIds.some((id) => id !== group.createdBy));
}

export function subscribeToChatTable(groupId, onChange, onError) {
  return onSnapshot(doc(db, 'groups', groupId), (snapshot) => {
    onChange(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
  }, onError);
}

// The latest 100 messages stay live; older messages can be loaded separately later.
export function subscribeToTableMessages(groupId, onChange, onError) {
  const messages = query(collection(db, 'groups', groupId, 'messages'), orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(messages, { includeMetadataChanges: true }, (snapshot) => {
    onChange(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), pending: item.metadata.hasPendingWrites })).reverse());
  }, onError);
}

export function newMessageId(groupId) {
  return doc(collection(db, 'groups', groupId, 'messages')).id;
}

export async function sendTableMessage(groupId, { id, text, senderName }) {
  const user = auth.currentUser;
  const body = text.trim();
  if (!user || user.isAnonymous) throw new Error('Accedi per scrivere al tavolo.');
  if (!body || body.length > MAX_MESSAGE_LENGTH) throw new Error('Scrivi un messaggio di massimo 2000 caratteri.');
  // Reuse the same ID on retry, so a slow connection cannot duplicate a message.
  await setDoc(doc(db, 'groups', groupId, 'messages', id), {
    senderId: user.uid,
    senderName: (senderName || 'Partecipante').slice(0, 120),
    text: body,
    createdAt: serverTimestamp(),
  });
}

// Consent is private to this account and shared across devices.
export function subscribeToCommunityConduct(userId, onChange, onError) {
  return onSnapshot(doc(db, 'users', userId, 'communityConsents', 'chat'),
    { includeMetadataChanges: true }, (snapshot) => {
      onChange(!snapshot.metadata.hasPendingWrites ? snapshot.data() || null : null);
    }, onError);
}

export async function acceptCommunityConduct() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error('Accedi per accettare il codice di condotta.');
  await setDoc(doc(db, 'users', user.uid, 'communityConsents', 'chat'), {
    version: COMMUNITY_CONDUCT_VERSION,
    acceptedAt: serverTimestamp(),
  });
}
